import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type CodeBlockMaxHeight = 'sm' | 'md' | 'lg' | 'xl'

interface CodeBlockProps {
    children: ReactNode
    variant?: 'default' | 'error'
    /** Cap the block's height and enable vertical scroll. `xl` is tuned for stack traces. */
    maxHeight?: CodeBlockMaxHeight
    /** Disable `whitespace-pre-wrap` — use for stack traces where wrapping garbles the frame layout. */
    preserveLines?: boolean
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const variantClasses = {
    default: 'bg-muted',
    error: 'bg-destructive/10 text-destructive',
}
const maxHeightClasses: Record<CodeBlockMaxHeight, string> = {
    sm: 'max-h-48 overflow-y-auto',
    md: 'max-h-64 overflow-y-auto',
    lg: 'max-h-96 overflow-y-auto',
    xl: 'max-h-[600px] overflow-y-auto',
}
/* eslint-enable i18next/no-literal-string */

export function CodeBlock({children, variant = 'default', maxHeight, preserveLines, className, 'data-testid': testId}: CodeBlockProps) {
    return (
        <pre
            className={cn(
                'font-mono text-xs p-3 rounded overflow-x-auto',
                !preserveLines && 'whitespace-pre-wrap',
                variantClasses[variant],
                maxHeight && maxHeightClasses[maxHeight],
                className,
            )}
            data-testid={testId}
        >
            {children}
        </pre>
    )
}
